/**
 * Login layout shell.
 *
 * Used by /login. Centered card on a subtle background. No nav, no top
 * bar — auth flows are their own context.
 *
 * Patch P22 — rebranded to TenderLens with brand block (lens icon +
 * name + tagline) and a "Powered by Solutyics" attribution in the
 * page footer.
 *
 * Patch — polished design: decorative blurred accent orbs in the
 * background, larger brand mark with an accent-coloured shadow glow,
 * roomier card with stronger elevation, and the vendor attribution
 * promoted to the brand accent colour so it reads as a deliberate
 * mark rather than incidental text.
 */
import type { ReactNode } from 'react';
import { ScanSearch } from 'lucide-react';
import { cn } from '@/lib/cn';

interface LoginLayoutProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

export function LoginLayout({ title, subtitle, children, footer, className }: LoginLayoutProps) {
  return (
    <div className="relative flex h-full flex-col items-center justify-center overflow-hidden bg-base p-6">
      {/* Decorative accent orbs — soft, low-opacity blurs in the brand
       * magenta. Purely visual; pointer-events-none so they never
       * intercept clicks. Positioned off-edge so they suggest depth
       * without dominating. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 -top-40 h-[28rem] w-[28rem] rounded-full bg-accent/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-accent/10 blur-3xl"
      />

      <div className={cn('relative w-[440px] max-w-full', className)}>
        {/* Brand block — larger, more confident mark with a subtle
         * accent-coloured glow under the icon tile. */}
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent text-accent-fg shadow-lg shadow-accent/30 ring-1 ring-accent-strong/20">
            <ScanSearch className="h-9 w-9" strokeWidth={1.75} />
          </div>
          <h2 className="mt-5 text-2xl font-semibold tracking-tight text-primary">TenderLens</h2>
          <p className="mt-2 max-w-[320px] text-sm leading-relaxed text-secondary">
            Procurement traceability — every tender, every batch, every stage.
          </p>
        </div>

        {/* Sign-in card — more padding, rounder corners, lifted shadow. */}
        <div className="rounded-xl border border-border-default bg-surface p-8 shadow-xl shadow-primary/5">
          <h1 className="text-lg font-semibold text-primary">{title}</h1>
          {subtitle ? <p className="mt-1 text-sm text-secondary">{subtitle}</p> : null}
          <div className="mt-6">{children}</div>
        </div>
        {footer ? <div className="mt-5 text-center text-xs text-tertiary">{footer}</div> : null}
      </div>

      {/* Vendor attribution. Lives on the page itself (not the card) so it
       * reads as "this product is provided by Solutyics" rather than
       * being part of the sign-in form. Promoted to the brand accent
       * colour so it carries weight as a signature mark. */}
      <div className="relative mt-10 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-accent">
        Powered by Solutyics
      </div>
    </div>
  );
}
